import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, X } from "lucide-react";
import { fileVideoUrl } from "../lib/api";

export default function VideoPlayer({ movie, onClose }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setProgress(v.currentTime);
      setDuration(v.duration || 0);
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onTime);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onTime);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * duration;
  };

  const fs = () => {
    const v = videoRef.current;
    if (!v) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      v.requestFullscreen?.();
    }
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "00:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div data-testid="video-player-overlay" className="fixed inset-0 z-[60] bg-black flex items-center justify-center">
      <button
        onClick={onClose}
        data-testid="video-player-close"
        className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:bg-[color:var(--ns-accent)] transition"
      >
        <X size={20} />
      </button>

      <video
        ref={videoRef}
        data-testid="video-player-element"
        src={fileVideoUrl(movie.video_path)}
        className="w-full h-full object-contain bg-black"
        controls={false}
        onEnded={() => setPlaying(false)}
        autoPlay
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent space-y-3">
        <div className="font-mono-mini text-xs text-[color:var(--ns-accent)] uppercase tracking-widest">
          {movie.is_4k ? "4K HDR" : "HD"} · Streaming <span className="notranslate" translate="no">KineBE</span>
        </div>
        <h3 className="font-display text-2xl text-white uppercase">{movie.title}</h3>

        <div onClick={seek} className="h-1.5 bg-white/15 rounded-full cursor-pointer group">
          <div
            className="h-full bg-[color:var(--ns-accent)] rounded-full"
            style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              data-testid="video-player-playpause"
              className="w-12 h-12 rounded-full btn-accent flex items-center justify-center"
            >
              {playing ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
            </button>
            <button
              onClick={toggleMute}
              data-testid="video-player-mute"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <span className="font-mono-mini text-xs text-zinc-300">
              {fmt(progress)} / {fmt(duration)}
            </span>
          </div>

          <button
            onClick={fs}
            data-testid="video-player-fullscreen"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
          >
            <Maximize size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
